import { useState, useEffect } from "react";
import { Mail, Loader2, Check, AlertCircle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const SESSION_LIMIT = 3;
const GLOBAL_LIMIT = 10;

interface EmailResultsButtonProps {
  toolId: string;
  toolName: string;
  results: object;
  userEmail: string | null;
  userName: string | null;
  sessionToken: string | null;
}

export const EmailResultsButton = ({
  toolId,
  toolName,
  results,
  userEmail,
  userName,
  sessionToken,
}: EmailResultsButtonProps) => {
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sessionRemaining, setSessionRemaining] = useState<number | null>(null);
  const [globalRemaining, setGlobalRemaining] = useState<number | null>(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetReason, setResetReason] = useState("");
  const [isRequestingReset, setIsRequestingReset] = useState(false);

  useEffect(() => {
    // Reset sent state when results change
    setSent(false);
  }, [results]);

  useEffect(() => {
    // Get remaining count from session storage as initial estimate
    const sentCount = parseInt(sessionStorage.getItem("itdukes_emails_sent") || "0", 10);
    setSessionRemaining(SESSION_LIMIT - sentCount);
    // Global remaining will be updated from server response
    setGlobalRemaining(GLOBAL_LIMIT);
  }, []);

  const handleSendEmail = async () => {
    if (!userEmail || !userName || !sessionToken) {
      toast.error("Please verify your email first to use this feature");
      return;
    }

    if ((sessionRemaining !== null && sessionRemaining <= 0) || 
        (globalRemaining !== null && globalRemaining <= 0)) {
      toast.error("You've reached the email limit");
      return;
    }

    setIsSending(true);

    try {
      const { data, error } = await supabase.functions.invoke("send-results-email", {
        body: {
          toolId,
          toolName,
          userEmail,
          userName,
          results,
          sessionToken,
        },
      });

      if (error) throw error;

      if (data.error) {
        if (data.limitType === "session") {
          setSessionRemaining(0);
          toast.error(`Session limit: ${SESSION_LIMIT} emails per session`);
        } else if (data.limitType === "global") {
          setGlobalRemaining(0);
          toast.error(`Email limit: ${GLOBAL_LIMIT} emails per email address`);
        } else {
          throw new Error(data.error);
        }
        return;
      }

      // Update local count
      const currentCount = parseInt(sessionStorage.getItem("itdukes_emails_sent") || "0", 10);
      sessionStorage.setItem("itdukes_emails_sent", String(currentCount + 1));
      
      setSessionRemaining(data.sessionRemaining);
      setGlobalRemaining(data.globalRemaining);
      setSent(true);
      toast.success("Results sent to your email!");
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to send email. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const handleRequestReset = async () => {
    if (!userEmail || !userName || !sessionToken || !resetReason.trim()) {
      toast.error("Please provide a reason for your request");
      return;
    }

    setIsRequestingReset(true);

    try {
      const { data, error } = await supabase.functions.invoke("request-limit-reset", {
        body: {
          email: userEmail,
          fullName: userName,
          reason: resetReason.trim(),
          sessionToken,
        },
      });

      if (error) throw error;

      if (data.error) {
        throw new Error(data.error);
      }

      toast.success("Request submitted! You'll be notified once reviewed.");
      setShowResetDialog(false);
      setResetReason("");
    } catch (error: any) {
      console.error("Error requesting reset:", error);
      toast.error(error.message || "Failed to submit request. Please try again.");
    } finally {
      setIsRequestingReset(false);
    }
  };

  const isDisabled = isSending || sent || !userEmail || 
    (sessionRemaining !== null && sessionRemaining <= 0) ||
    (globalRemaining !== null && globalRemaining <= 0);

  // Show the more restrictive remaining count
  const effectiveRemaining = sessionRemaining !== null && globalRemaining !== null
    ? Math.min(sessionRemaining, globalRemaining)
    : sessionRemaining ?? globalRemaining;

  const isLimitReached = effectiveRemaining !== null && effectiveRemaining <= 0;

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={handleSendEmail}
          disabled={isDisabled}
          className="gap-2"
        >
          {isSending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Sending...
            </>
          ) : sent ? (
            <>
              <Check className="w-4 h-4 text-success" />
              Sent!
            </>
          ) : (
            <>
              <Mail className="w-4 h-4" />
              Email Results
            </>
          )}
        </Button>
        {effectiveRemaining !== null && effectiveRemaining > 0 && !sent && (
          <span className="text-xs text-muted-foreground">
            {effectiveRemaining} email{effectiveRemaining !== 1 ? "s" : ""} left
          </span>
        )}
        {isLimitReached && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Limit reached
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowResetDialog(true)}
              className="text-xs h-7 px-2 text-primary hover:text-primary"
            >
              <RotateCcw className="w-3 h-3 mr-1" />
              Request Reset
            </Button>
          </div>
        )}
      </div>

      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request Email Limit Reset</DialogTitle>
            <DialogDescription>
              Please explain why you need more emails. An admin will review your request.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              placeholder="e.g., I'm testing multiple email campaigns and need additional sends..."
              value={resetReason}
              onChange={(e) => setResetReason(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowResetDialog(false)}
              disabled={isRequestingReset}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRequestReset}
              disabled={isRequestingReset || !resetReason.trim()}
            >
              {isRequestingReset ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                "Submit Request"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
